import { coloresDe, porcentaje, entero, ORO, NEUTRO, RESULTADO, esVago } from '../lib/graficas';

/**
 * Barras horizontales en HTML (0.46.0), para los detalles (perfil, conclusiones) y las listas cortas: cada fila es
 * nombre · barra · "N · X %". Se leen en celular sin girar etiquetas, cada fila se puede tocar (es un botón cuando hay
 * `onAbrir`) y no dependen de que el texto quepa en un eje. "Se sabe de N de M" va arriba cuando no es de todos.
 */
export interface FilaBarra { clave: string; nombre: string; n: number; extra?: string; color?: string; titulo?: string }

const CSS = `
.br { display: grid; gap: 4px; min-width: 0; }
.br-sabe { font-size: 11.5px; color: var(--ink-soft); margin-bottom: 2px; }
.br-fila { display: grid; grid-template-columns: minmax(0, min(14em, 34%)) minmax(0, 1fr) min(var(--br-val, 8em), 46%); gap: 8px; align-items: center; font-size: 12.5px; padding: 3px 4px; margin: 0 -4px; border-radius: 8px; border: none; background: transparent; box-shadow: none; font-family: inherit; color: var(--ink); text-align: left; width: calc(100% + 8px); }
button.br-fila { cursor: pointer; }
button.br-fila:hover { background: var(--bg-alt); transform: none; }
button.br-fila:focus-visible { outline: 2px solid var(--gold); outline-offset: 1px; }
.br-nom { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.br-pista { height: 12px; background: rgba(0,0,0,.05); border-radius: 4px; overflow: hidden; display: flex; gap: 2px; }
.br-pista > i { display: block; height: 100%; border-radius: 4px; min-width: 0; }
.br-val { text-align: right; font-variant-numeric: tabular-nums; line-height: 1.3; min-width: 0; }
.br-val > .m { white-space: nowrap; }
.br-val small { color: var(--ink-soft); font-size: 11px; }
.br-val > small.x::before { content: ' · '; }
/* En celular el nombre va arriba, completo, y la barra con su número abajo: cortar "Negocios, v…" no dice nada. */
@media (max-width: 480px) {
  .br-fila { grid-template-columns: minmax(0, 1fr) min(var(--br-val, 8em), 55%); grid-template-areas: "nom nom" "pista val"; row-gap: 2px; padding: 4px; }
  .br-nom { grid-area: nom; white-space: normal; overflow: visible; }
  .br-pista { grid-area: pista; }
  .br-val { grid-area: val; }
  /* Lo de más ("73 % del anterior") va en su propio renglón, bajo la barra: al lado del número se comía la barra o se
     partía en tres renglones. */
  .br-fila { grid-template-columns: minmax(0, 1fr) min(var(--br-val-m, 8em), 55%); grid-template-areas: "nom nom" "pista val" "x x"; }
  .br-val { display: contents; }
  .br-val > .m { grid-area: val; text-align: right; }
  .br-val > small.x { grid-area: x; white-space: normal; line-height: 1.3; }
  .br-val > small.x::before { content: none; }
}
.br-ley { display: flex; gap: 12px; flex-wrap: wrap; font-size: 11.5px; color: var(--ink-soft); margin-top: 4px; }
.br-ley i { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }
`;

export function Barras({ filas, total, sabe, de, color, colores: fijos, onAbrir, formato, vacio = 'Nadie lo tiene capturado todavía.', datos }: {
  filas: FilaBarra[]; total?: number; sabe?: number; de?: number; color?: string; colores?: Record<string, string>;
  onAbrir?: (clave: string) => void; formato?: (f: FilaBarra, tot: number) => string; vacio?: string; datos?: string;
}) {
  const tot = total ?? filas.reduce((a, f) => a + f.n, 0);
  const max = Math.max(1, ...filas.map((f) => f.n));
  const cols = fijos ? coloresDe(filas, fijos) : null;
  // La columna de los números mide lo mismo en todas las filas (la del texto más largo): así las barras empiezan y
  // terminan parejo aunque una fila diga más que otra.
  const principal = (f: FilaBarra) => (formato ? formato(f, tot) : `${entero(f.n)} · ${porcentaje(f.n, tot)}`).length * 0.6;
  const largo = Math.max(...filas.map((f) => principal(f) + (f.extra ? f.extra.length * 0.52 : 0)), 3);
  const largoM = Math.max(...filas.map(principal), 3);
  return (
    <div className="br" data-barras={datos || ''} style={{ ['--br-val' as any]: `${Math.min(largo, 24).toFixed(1)}em`, ['--br-val-m' as any]: `${Math.min(largoM, 16).toFixed(1)}em` }}>
      <style>{CSS}</style>
      {sabe != null && de != null && <div className="br-sabe" data-sabe>Se sabe de {entero(sabe)} de {entero(de)}{de ? ` (${porcentaje(sabe, de)})` : ''}</div>}
      {filas.length === 0 && <div className="muted" style={{ fontSize: 12.5 }}>{vacio}</div>}
      {filas.map((f) => {
        const c = f.color || cols?.get(f.clave) || (esVago(f.nombre) ? NEUTRO : color || ORO);
        const cont = (
          <>
            <span className="br-nom" title={f.titulo || f.nombre}>{f.nombre}</span>
            <span className="br-pista"><i style={{ width: `${(f.n / max) * 100}%`, background: c }} /></span>
            <span className="br-val"><span className="m">{formato ? formato(f, tot) : <>{entero(f.n)} <small>· {porcentaje(f.n, tot)}</small></>}</span>{f.extra ? <small className="x">{f.extra.replace(/^·\s*/, '')}</small> : null}</span>
          </>
        );
        return onAbrir
          ? <button key={f.clave} type="button" className="br-fila" onClick={() => onAbrir(f.clave)} title={`Ver: ${f.titulo || f.nombre}`} data-barra={f.clave}>{cont}</button>
          : <div key={f.clave} className="br-fila" data-barra={f.clave}>{cont}</div>;
      })}
    </div>
  );
}

/** Una fila por grupo con su resultado apilado al 100 %: llegó (verde) · canceló (naranja) · no llegó (gris). */
export interface FilaResultado { clave: string; nombre: string; n: number; llego: number; cancelo: number; noLlego: number; pct: number; pocos?: boolean }
export function BarrasResultado({ filas, onAbrir, datos }: { filas: FilaResultado[]; onAbrir?: (clave: string) => void; datos?: string }) {
  const largo = Math.max(...filas.map((f) => `${f.pct} %`.length * 0.62 + `de ${entero(f.n)}${f.pocos ? ' · pocos casos' : ''}`.length * 0.52), 3);
  return (
    <div className="br" data-barras-resultado={datos || ''} style={{ ['--br-val' as any]: `${Math.min(largo, 24).toFixed(1)}em` }}>
      <style>{CSS}</style>
      {filas.map((f) => {
        const seg = (x: number) => `${f.n ? (x / f.n) * 100 : 0}%`;
        const cont = (
          <>
            <span className="br-nom" title={f.nombre}>{f.nombre}</span>
            <span className="br-pista" style={{ opacity: f.pocos ? 0.55 : 1 }} aria-hidden="true">
              {f.llego > 0 && <i style={{ width: seg(f.llego), background: RESULTADO.llego }} />}
              {f.cancelo > 0 && <i style={{ width: seg(f.cancelo), background: RESULTADO.cancelo }} />}
              {f.noLlego > 0 && <i style={{ width: seg(f.noLlego), background: RESULTADO.noLlego }} />}
            </span>
            <span className="br-val"><span className="m"><b>{f.pct} %</b> <small>de {entero(f.n)}</small></span>{f.pocos ? <small> · pocos casos</small> : null}</span>
          </>
        );
        const titulo = `${f.nombre}: ${f.llego} llegaron, ${f.cancelo} cancelaron, ${f.noLlego} no llegaron (de ${f.n})`;
        return onAbrir
          ? <button key={f.clave} type="button" className="br-fila" onClick={() => onAbrir(f.clave)} title={titulo} aria-label={titulo} data-barra={f.clave}>{cont}</button>
          : <div key={f.clave} className="br-fila" title={titulo} data-barra={f.clave}>{cont}</div>;
      })}
      <div className="br-ley" aria-hidden="true"><span><i style={{ background: RESULTADO.llego }} />Llegó</span><span><i style={{ background: RESULTADO.cancelo }} />Canceló</span><span><i style={{ background: RESULTADO.noLlego }} />No llegó</span></div>
    </div>
  );
}
